import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { getNegotiationAgreement } from "../../api/caseNegotiations";
import useErrorHandler from "../../hooks/useErrorHandler";
import { BASIS_TEXT, fmtDateTime, toUSD } from "./negotiation-ui";

function Row({ label, children }) {
  return (
    <Box className="flex flex-col gap-0.5 border-0 border-t border-solid border-[#e3e7ee] py-3 min-[600px]:flex-row min-[600px]:gap-6">
      <Typography fontSize="small" color="text.secondary" className="min-[600px]:w-[180px] shrink-0 uppercase tracking-[0.05em]">
        {label}
      </Typography>
      <Box className="flex-1">{children}</Box>
    </Box>
  );
}

// Printable settlement agreement. Rendered outside the Layout so nothing but the document prints.
export default function SettlementAgreement() {
  const { caseId, negId } = useParams();
  const navigate = useNavigate();
  const { data: user } = useSelector((state) => state.user);
  const errorHandler = useErrorHandler();
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notSettled, setNotSettled] = useState(false);

  useEffect(() => {
    if (!user.token) return;
    setLoading(true);
    getNegotiationAgreement(user.token, caseId, negId)
      .then(({ data }) => setAgreement(data.data))
      .catch((error) => {
        if (error?.response?.status === 409) setNotSettled(true);
        else errorHandler(error);
      })
      .finally(() => setLoading(false));
  }, [caseId, negId, user.token]);

  const goBack = () => navigate(`/negotiation/${caseId}/${negId}`);
  const s = agreement?.settlement;
  const parties = agreement?.parties || [];
  const bySide = (side) => parties.filter((p) => p.side === side);

  return (
    <Box className="min-h-screen bg-[#f4f6f9] px-4 py-6 print:bg-white print:p-0">
      {/* toasts and alerts are rendered by the route wrapper, outside this page: keep them off the printout */}
      <style>{"@media print { .MuiSnackbar-root, .MuiDialog-root { display: none !important; } }"}</style>
      <Box className="mx-auto mb-4 flex max-w-[820px] items-center justify-between print:hidden">
        <Button variant="outlined" onClick={goBack} className="normal-case">
          Back to negotiation
        </Button>
        {agreement ? (
          <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()} className="normal-case">
            Print
          </Button>
        ) : null}
      </Box>

      {loading ? (
        <Box className="py-16 grid place-items-center">
          <CircularProgress size={28} />
        </Box>
      ) : !agreement ? (
        <Box className="mx-auto max-w-[820px] rounded-xl bg-white p-8 text-center shadow-sm">
          <Typography fontWeight={600}>
            {notSettled ? "There is no settlement agreement yet." : "The settlement agreement could not be loaded."}
          </Typography>
          <Typography color="text.secondary" className="mt-1">
            {notSettled
              ? "An agreement is available once the negotiation has been settled."
              : "Go back to the negotiation and try again."}
          </Typography>
        </Box>
      ) : (
        <Box className="mx-auto max-w-[820px] rounded-xl bg-white p-10 shadow-sm print:max-w-none print:rounded-none print:p-0 print:shadow-none">
          <Box className="text-center">
            <Typography fontSize="small" color="text.secondary" className="uppercase tracking-[0.12em]">
              Online Dispute Resolution
            </Typography>
            <Typography variant="h4" className="!mt-1 !font-semibold">
              Settlement Agreement
            </Typography>
            <Typography fontSize="small" color="text.secondary" className="!mt-1">
              Reference {agreement.reference} · Generated {fmtDateTime(agreement.generatedAt)}
            </Typography>
          </Box>

          <Box className="mt-6">
            <Row label="Case">
              <Typography fontWeight={600}>{agreement.case?.title}</Typography>
              {agreement.case?.type ? <Typography fontSize="small">{agreement.case.type}</Typography> : null}
            </Row>
            <Row label="Parties">
              {["PLAINTIFF", "DEFENDANT"].map((side) =>
                bySide(side).map((p) => (
                  <Typography key={`${side}-${p.email}`}>
                    <b>{p.name}</b> {p.email ? `(${p.email})` : ""} <span className="text-[#6b7a90]">· {side === "PLAINTIFF" ? "Plaintiff" : "Defendant"}</span>
                  </Typography>
                ))
              )}
            </Row>
            {agreement.arbitrator ? (
              <Row label="Arbitrator">
                <Typography>{typeof agreement.arbitrator === "string" ? agreement.arbitrator : agreement.arbitrator.name}</Typography>
              </Row>
            ) : null}
            <Row label="Negotiation">
              <Typography fontWeight={600}>{agreement.negotiation?.title}</Typography>
              {agreement.negotiation?.description ? (
                <Typography fontSize="small" className="whitespace-pre-wrap">
                  {agreement.negotiation.description}
                </Typography>
              ) : null}
            </Row>
            {s?.monetary !== false ? (
              <Row label="Settled amount">
                <Typography variant="h5" className="!font-semibold">
                  {toUSD(s?.amount)}
                </Typography>
              </Row>
            ) : (
              <Row label="Settlement">
                <Typography fontWeight={600}>Settled on the agreed terms below. No money is involved.</Typography>
              </Row>
            )}
            {s?.items?.length ? (
              <Row label="Agreed terms">
                {s.items.map((i, idx) => (
                  <Typography key={`${i.type}-${idx}`}>
                    <b>{i.label}:</b> {i.value}
                  </Typography>
                ))}
              </Row>
            ) : null}
            <Row label="Other terms">
              <Typography className="whitespace-pre-wrap">{s?.terms || "None."}</Typography>
            </Row>
            <Row label="Basis">
              <Typography>{BASIS_TEXT[s?.basis] || BASIS_TEXT.OFFER}</Typography>
              {s?.offeredBy ? <Typography fontSize="small">Offered by {s.offeredBy}</Typography> : null}
            </Row>
            <Row label="Acceptance">
              <Typography>
                Accepted by {s?.acceptedBy || "-"} on {fmtDateTime(s?.acceptedAt)}
              </Typography>
              {s?.closedCase ? <Typography fontSize="small">The case was closed on settlement.</Typography> : null}
            </Row>
          </Box>

          <Box className="mt-14 grid grid-cols-1 gap-10 min-[600px]:grid-cols-2">
            {["PLAINTIFF", "DEFENDANT"].map((side) => (
              <Box key={side}>
                <Box className="h-10 border-0 border-b border-solid border-[#333]" />
                <Typography fontSize="small" className="!mt-1">
                  {side === "PLAINTIFF" ? "Plaintiff" : "Defendant"}
                  {bySide(side)[0] ? `: ${bySide(side)[0].name}` : ""}
                </Typography>
                <Typography fontSize="small" color="text.secondary">
                  Date:
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
